import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { CheckCircle2, Loader2, XCircle } from "lucide-react";
import { api } from "@/lib/api";

const PaymentResult = ({ status }) => {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get("session_id");
  const [paid, setPaid] = useState(false);
  const [tries, setTries] = useState(0);

  useEffect(() => {
    if (status !== "success" || !sessionId) return;
    let alive = true;
    let n = 0;
    const iv = setInterval(async () => {
      n += 1;
      setTries(n);
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (alive && data.payment_status === "paid") {
          setPaid(true);
          clearInterval(iv);
        }
      } catch {
        /* keep polling */
      }
      if (n >= 15 && alive) clearInterval(iv);
    }, 2000);
    return () => {
      alive = false;
      clearInterval(iv);
    };
  }, [sessionId, status]);

  const success = status === "success" && (paid || tries >= 15);

  return (
    <div className="min-h-screen bg-[#050507] text-white flex items-center justify-center p-8 relative overflow-hidden">
      <div className="absolute -top-32 left-1/3 w-[480px] h-[480px] rounded-full bg-[#D65BB4] opacity-[0.12] blur-[140px]" />
      <div className="absolute bottom-0 right-0 w-[420px] h-[420px] rounded-full bg-[#4DBDBE] opacity-[0.1] blur-[140px]" />
      <div className="relative z-10 text-center max-w-md">
        <img src="/images/logo-vice.jpg" alt="DJ Miami" className="w-32 mx-auto mb-10" data-testid="payment-logo-img" />
        {status === "success" ? (
          <>
            {paid ? (
              <CheckCircle2 size={56} className="mx-auto text-[#4DBDBE] mb-6" data-testid="payment-success-icon" />
            ) : (
              <Loader2 size={56} className="mx-auto text-white/60 animate-spin mb-6" data-testid="payment-loading-icon" />
            )}
            <h1 className="font-display font-black uppercase text-3xl mb-4" data-testid="payment-success-title">
              {paid ? "Payment complete." : "Confirming your payment…"}
            </h1>
            <p className="text-white/60 leading-relaxed">
              {paid
                ? "Your license is locked in. Head back to the site, tap My Downloads and search the email you used — your purchase is on record."
                : "One moment — we're confirming with the payment processor. This usually takes a few seconds."}
            </p>
          </>
        ) : (
          <>
            <XCircle size={56} className="mx-auto text-[#D65BB4] mb-6" data-testid="payment-cancel-icon" />
            <h1 className="font-display font-black uppercase text-3xl mb-4" data-testid="payment-cancel-title">
              Payment canceled.
            </h1>
            <p className="text-white/60 leading-relaxed">
              No charges went through. The vault is still open — tap back in whenever you're ready.
            </p>
          </>
        )}
        <a
          href="/"
          data-testid="payment-back-home-btn"
          className="inline-block mt-10 border border-white/25 px-8 py-4 text-xs uppercase tracking-widest font-bold hover:border-[#4DBDBE] hover:text-[#4DBDBE] transition-colors"
        >
          Back to the site
        </a>
      </div>
    </div>
  );
};

export default PaymentResult;
